/**
 * TUS Upload Service for resumable, chunk-based uploads
 * 
 * Uses tus-js-client configured for Supabase Storage
 * - 6MB chunks for memory efficiency
 * - Exponential backoff retry
 * - Auto-resume from previous uploads via TUS fingerprinting
 */

import * as tus from 'tus-js-client';
import { supabase } from '@/integrations/supabase/client';

const SUPABASE_PROJECT_REF = 'sekiwtiutnikmdbpiufi';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InNla2l3dGl1dG5pa21kYnBpdWZpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzUzOTI0MzIsImV4cCI6MjA1MDk2ODQzMn0.OYCXJ_wPnVWdgSeTHGc_fHaHP5pHhsGBt1TN2zeLz0M';
const TUS_ENDPOINT = `https://${SUPABASE_PROJECT_REF}.supabase.co/storage/v1/upload/resumable`;

// 6MB chunk size - balances memory vs request overhead
const CHUNK_SIZE = 6 * 1024 * 1024;

// Retry delays with exponential backoff (ms)
const RETRY_DELAYS = [0, 1000, 3000, 5000, 10000, 20000];

export interface TusUploadOptions {
  blob: Blob;
  bucketName: string;
  filePath: string;
  contentType: string;
  onProgress?: (progress: number, bytesUploaded: number, bytesTotal: number) => void;
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}

export interface TusUploadResult {
  success: boolean;
  filePath?: string;
  error?: Error;
}

/**
 * Get current auth token, refreshing if needed
 */
async function getAuthToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session?.access_token) {
    // Try to refresh
    const { data: refreshed } = await supabase.auth.refreshSession();
    return refreshed.session?.access_token || null;
  }
  
  return session.access_token;
}

/**
 * Upload a blob using TUS protocol for resumable uploads
 * Returns the file path on success
 */
export function uploadWithTus(options: TusUploadOptions): Promise<TusUploadResult> {
  const { blob, bucketName, filePath, contentType, onProgress, onError, onSuccess } = options;

  return new Promise(async (resolve) => {
    const token = await getAuthToken();
    
    if (!token) {
      const error = new Error('No valid auth session');
      onError?.(error);
      resolve({ success: false, error });
      return;
    }

    const upload = new tus.Upload(blob, {
      endpoint: TUS_ENDPOINT,
      retryDelays: RETRY_DELAYS,
      chunkSize: CHUNK_SIZE,
      headers: {
        authorization: `Bearer ${token}`,
        apikey: SUPABASE_ANON_KEY,
      },
      uploadDataDuringCreation: true,
      removeFingerprintOnSuccess: true,
      metadata: {
        bucketName,
        objectName: filePath,
        contentType,
        cacheControl: '3600',
      },
      onError: (error) => {
        console.error('[TUS] Upload error:', error);
        onError?.(error);
        resolve({ success: false, error });
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        const percentage = Math.round((bytesUploaded / bytesTotal) * 100);
        onProgress?.(percentage, bytesUploaded, bytesTotal);
      },
      onSuccess: () => {
        console.log('[TUS] Upload complete:', filePath);
        onSuccess?.();
        resolve({ success: true, filePath });
      },
    });

    // Check for previous partial uploads (resumability!)
    try {
      const previousUploads = await upload.findPreviousUploads();
      if (previousUploads.length > 0) {
        console.log('[TUS] Resuming previous upload for:', filePath);
        upload.resumeFromPreviousUpload(previousUploads[0]);
      }
    } catch (e) {
      // Fingerprint lookup failed, start fresh
      console.log('[TUS] No previous upload found, starting fresh');
    }

    upload.start();
  });
}

/**
 * Abort a TUS upload
 */
export function abortTusUpload(upload: tus.Upload): void {
  try {
    upload.abort();
    console.log('[TUS] Upload aborted');
  } catch (e) {
    console.warn('[TUS] Error aborting upload:', e);
  }
}

/**
 * Calculate estimated upload time based on file size and connection
 */
export function estimateUploadTime(fileSize: number, connectionSpeedMbps: number = 5): number {
  // Convert Mbps to bytes per second
  const bytesPerSecond = (connectionSpeedMbps * 1000 * 1000) / 8;
  return Math.ceil(fileSize / bytesPerSecond);
}
